import { ReactNode } from 'react';
import {
	Anchor,
	Badge,
	Image,
	Burger,
	Button,
	Center,
	Divider,
	Group,
	MantineProvider as Mantine,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Pagination,
	Radio,
	RadioGroup,
	Select,
	Tooltip,
	Notification,
	Stack,
	Checkbox,
	Popover,
	Accordion,
	BackgroundImage,
	Title,
	Drawer,
	Highlight,
	RangeSlider,
	Slider,
	Progress,
	Overlay,
	LoadingOverlay,
	PasswordInput,
	Text,
	Textarea,
	TextInput,
	Loader as MantineLoader,
	NumberInput,
	SegmentedControl,
	Chip,
	Table,
	ColorInput,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { DateInput, DatePicker } from '@mantine/dates';
import { Loader } from '@/components/ui/loading/partials/Loader';
import { IconChevronDown, IconEye, IconX } from '@tabler/icons-react';
import APP_CONFIG from '@/configs/appConfig';
import classNames from 'classnames';
import styles from '../sass/mantine.module.scss';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';
import '@mantine/notifications/styles.layer.css';

const colors: MantineThemeOverride['colors'] = {
	red: [
		'#ffedec',
		'#f6d9d8',
		'#eab0af',
		'#e08582',
		'#d7605c',
		'#d24944',
		'#d03d38',
		'#b82f2b',
		'#ab2926',
		'#911d1d',
	],
	blue: [
		'#f0f3fa',
		'#dfe4ed',
		'#bac6dd',
		'#92a6ce',
		'#718bc1',
		'#5c7aba',
		'#5172b7',
		'#4261a1',
		'#395691',
		'#233a65',
	],
};

const components: MantineThemeOverride['components'] = {
	Chip: Chip.extend({
		defaultProps: {
			color: 'blue.9',
			radius: 0,
			size: 'md',
			icon: null,
		},
		classNames: {
			label: styles.chipLabel,
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {
			withControls: true,
			gap: 'sm',
			radius: 3,
		},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Text: Text.extend({
		defaultProps: {
			color: '#474747',
		},
	}),
	Burger: Burger.extend({
		defaultProps: {
			color: 'red',
			size: 30,
		},
	}),
	Badge: Badge.extend({
		defaultProps: {
			radius: 0,
			px: 'sm',
			py: 'xs',
			color: 'dark.6',
		},
		classNames: {
			label: styles.badgeLabel,
		},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			withCloseButton: true,
			radius: 4,
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
			closeButtonProps: {
				size: 24,
				icon: <IconX />,
			},
			size: APP_CONFIG.BREAKPOINTS.XL,
			closeOnClickOutside: false,
		},
		classNames: {
			root: styles.modalRoot,
			inner: styles.modalInner,
			content: styles.modalContent,
			header: styles.modalHeader,
			body: styles.modalBody,
			close: styles.modalClose,
			title: styles.modalTitle,
		},
	}),
	SegmentedControl: SegmentedControl.extend({
		defaultProps: {
			color: 'red.4',
			c: 'white',
			size: 'md',
			radius: 0,
			p: 0,
		},
		classNames: {
			root: styles.segmentedControlRoot,
			label: styles.segmentedControlLabel,
			innerLabel: styles.segmentedControlInnerLabel,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			radius: 5,
			loaderProps: {
				type: 'dots',
			},
		},
	}),
	Menu: Menu.extend({
		defaultProps: {
			shadow: 'md',
			radius: 0,
		},
		classNames: {
			itemLabel: styles.menuItemLabel,
		},
	}),
	Divider: Divider.extend({
		defaultProps: {
			labelPosition: 'left',
			size: 'xs',
		},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Select: Select.extend({
		defaultProps: {
			radius: 0,
			size: 'md',
			withCheckIcon: false,
			allowDeselect: false,
			scrollAreaProps: {
				type: 'auto',
			},
			comboboxProps: {
				transitionProps: {
					duration: 100,
					transition: 'scale-y',
					timingFunction: 'ease',
				},
				zIndex: APP_CONFIG.Z_INDEX.SELECT,
			},
		},
		classNames: {
			input: styles.selectInput,
			label: styles.selectLabel,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
		},
	}),
	Image: Image.extend({
		defaultProps: {
			radius: 3,
			fit: 'contain',
		},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {
			rightSection: <IconChevronDown />,
			radius: 4,
			size: 'lg',
		},
		classNames: {
			input: styles.selectInput,
			section: styles.selectRightSection,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			position: 'top',
			transitionProps: {
				transition: 'pop',
				duration: 200,
			},
			multiline: true,
			withArrow: true,
			arrowSize: 6,
			events: { hover: true, focus: true, touch: true },
			color: 'black',
			opacity: 0.9,
			radius: 3,
			zIndex: APP_CONFIG.Z_INDEX.TOOLTIP,
		},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({}),
	Radio: Radio.extend({
		defaultProps: {
			size: 'xs',
		},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			underline: 'always',
			c: 'black',
			fz: 16,
			fw: 400,
		},
	}),
	Stack: Stack.extend({
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		classNames: {
			inner: styles.checkboxInner,
			label: styles.checkboxLabel,
		},
		defaultProps: {
			size: 'lg',
			color: 'dark',
			radius: 4,
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 5,
			size: 'md',
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 0,
			size: 'md',
			hideControls: true,
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			size: 'md',
			radius: 0,
			autosize: true,
			minRows: 4,
			maxRows: 8,
		},
		classNames: {
			label: styles.textInputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			radius: 3,
			visibilityToggleIcon: ({ reveal }) => (
				<Center
					className={classNames({
						[styles.passwordInputIconRevealed]: reveal,
					})}
				>
					<IconEye />
				</Center>
			),
		},
		classNames: {
			wrapper: styles.passwordInputWrapper,
			input: styles.passwordInputInput,
			section: styles.passwordInputSection,
			error: styles.passwordInputError,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {
			loaders: { ...MantineLoader.defaultLoaders, spinner: Loader },
			type: 'spinner',
		},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {
			loaderProps: {
				type: 'spinner',
			},
			overlayProps: {
				opacity: 0.5,
			},
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Overlay: Overlay.extend({
		defaultProps: {
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
			backgroundOpacity: 0.25,
		},
	}),
	Progress: Progress.extend({
		defaultProps: {
			size: 'md',
			color: 'green.6',
			radius: 'md',
		},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			thumbSize: 22,
			size: 6,
		},
		classNames: {
			root: styles.sliderRoot,
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
			markLabel: styles.sliderMarkLabel,
		},
	}),
	RangeSlider: RangeSlider.extend({
		defaultProps: {
			color: 'dark.5',
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			minRange: 1,
		},
	}),
	Highlight: Highlight.extend({
		defaultProps: {
			color: 'dark',
			highlightStyles: {
				fontWeight: 700,
			},
		},
	}),
	DatePicker: DatePicker.extend({
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	DateInput: DateInput.extend({
		defaultProps: {
			locale: 'pl',
			valueFormat: 'MMMM D, YYYY',
		},
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			striped: true,
			withTableBorder: true,
		},
		classNames: {},
	}),
	Drawer: Drawer.extend({
		defaultProps: {
			position: 'left',
			zIndex: APP_CONFIG.Z_INDEX.DRAWER,
			size: 492,
			withCloseButton: true,
		},
		classNames: {
			overlay: styles.drawerInner,
			content: styles.drawerContent,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {
			lts: -0.25,
			c: 'gray.8',
		},
	}),
	Notification: Notification.extend({
		defaultProps: { radius: 3, withCloseButton: false },
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			icon: styles.notificationIcon,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {},
	}),
	Accordion: Accordion.extend({
		defaultProps: {
			chevronPosition: 'left',
			chevronSize: 24,
			chevron: (
				<IconChevronDown
					className="icon-gray400"
					width="1.5rem"
					height="1.5rem"
				/>
			),
			transitionDuration: 250,
		},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			radius: 5,
			zIndex: APP_CONFIG.Z_INDEX.POPOVER,
			withArrow: true,
			shadow: 'md',
		},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
	ColorInput: ColorInput.extend({
		defaultProps: {
			// Prefer StableColorInput for app color fields. Keep these defaults for any
			// remaining ColorInput so blur cannot revert a just-picked controlled value.
			fixOnBlur: false,
			withEyeDropper: false,
			closeOnColorSwatchClick: true,
			popoverProps: {
				withinPortal: true,
				zIndex: APP_CONFIG.Z_INDEX.MODAL_INSTRUCTION,
				middlewares: { shift: true, flip: true },
			},
		},
	}),
};

const headings: MantineThemeOverride['headings'] = {
	fontWeight: '600',
	sizes: {},
};

const other: MantineThemeOverride['other'] = {};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1];
	return acc;
}, {});

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Mantine
			theme={{
				fontFamily: 'Yantramanav, sans-serif',
				white: '#ffffff',
				black: '#141414',
				colors,
				other,
				cursorType: 'pointer',
				components,
				headings,
				breakpoints,
				primaryColor: 'red',
				primaryShade: 8,
			}}
		>
			<Notifications position="top-center" limit={3} autoClose={4500} />
			{children}
		</Mantine>
	);
};
